import filter from 'lodash/filter';
import first from 'lodash/first';

import {
  AutoReply,
  AutoRepliesConfig,
  AutoReplyMessageTemplate,
  AutoReplyV2,
} from '@app/components/Admin/types';
import { AUTO_REPLY_TYPES } from '@app/constants/user-mgmt';
import { AuthState } from '@app/types/reducer-state';

export const convertToAutoReplyV1Type = (type: string) => {
  if (!process.env.FEATURE_FLAG_AUTO_REPLIES_V2) {
    return type;
  }
  if (type === AUTO_REPLY_TYPES.OUT_OF_OFFICE) {
    return AUTO_REPLY_TYPES.GENERAL;
  } else if (type === AUTO_REPLY_TYPES.OFFICE_HOURS) {
    return AUTO_REPLY_TYPES.OUT_OF_OFFICE;
  }
  return type;
};

export const convertToAutoReplyV2Type = (type: string) => {
  if (!process.env.FEATURE_FLAG_AUTO_REPLIES_V2) {
    return type;
  }
  if (type === AUTO_REPLY_TYPES.GENERAL) {
    return AUTO_REPLY_TYPES.OUT_OF_OFFICE;
  } else if (type === AUTO_REPLY_TYPES.OUT_OF_OFFICE) {
    return AUTO_REPLY_TYPES.OFFICE_HOURS;
  }
  return type;
};

/**
 * Determines user status based on auto-reply active state.
 * Returns 'away' if the auto-reply is active, 'active' otherwise.
 */
export const whatStatus = (autoReply: { active?: boolean } | undefined) =>
  autoReply?.active ? 'away' : 'active';

export const formatAutoReplyPayload = (
  autoReply: AutoReply & { type?: string },
) => {
  if (!process.env.FEATURE_FLAG_AUTO_REPLIES_V2) {
    return autoReply;
  }

  // Transform to schema-conforming payload
  return {
    type: convertToAutoReplyV2Type(autoReply?.type ?? ''),
    active: autoReply?.active,
    message: {
      ...(autoReply?.message?.during?.value && {
        during: {
          body: autoReply?.message?.during?.value,
          displayName: autoReply?.title,
          tokens: [],
        },
      }),
      ...(autoReply?.message?.after?.value && {
        after: {
          body: autoReply?.message?.after?.value,
          displayName: autoReply?.title,
          tokens: [],
        },
      }),
    },
    sendCriteria: {
      timeZone: autoReply?.sendCriteria?.timeZone,
      minutesInactiveThreshold:
        autoReply?.sendCriteria?.minutesInactiveThreshold,
      timing: autoReply?.sendCriteria?.timing,
      days: autoReply?.sendCriteria?.days,
    },
  };
};

export const formatAutoReplyResponse = (
  autoReply: AutoRepliesConfig,
  pk: string,
) => {
  if (!process.env.FEATURE_FLAG_AUTO_REPLIES_V2) {
    return autoReply;
  }
  if (!autoReply || !autoReply.autoReplies) {
    return [];
  }

  const currentTime = new Date().toISOString();

  const { disableOooMessage, disableAwayMessages } = autoReply || {};

  return autoReply.autoReplies.map((reply) => {
    // Transform message structure from v2 to v1
    const transformMessage = (msg: AutoReplyMessageTemplate) => ({
      type: 'STRING',
      value: msg?.body,
    });

    // Get title from message displayName (prefer 'after' if available)
    const title =
      reply?.message?.after?.displayName ||
      reply?.message?.during?.displayName ||
      '';

    // Get id from message (prefer 'after' if available)
    const id = reply?.message?.after?._id || reply?.message?.during?._id || '';

    return {
      sendCriteria: {
        timeZone: reply?.sendCriteria?.timeZone,
        minutesInactiveThreshold: reply?.sendCriteria?.minutesInactiveThreshold,
        days: reply?.sendCriteria?.days,
        timing: reply?.sendCriteria?.timing,
      },
      active:
        reply?.type === AUTO_REPLY_TYPES.OUT_OF_OFFICE
          ? !disableOooMessage
          : !disableAwayMessages,
      updatedAt: currentTime,
      createdAt: currentTime,
      message: {
        ...(reply?.message?.during && {
          during: transformMessage(reply.message.during),
        }),
        ...(reply?.message?.after && {
          after: transformMessage(reply.message.after),
        }),
      },
      pk,
      title,
      type: convertToAutoReplyV1Type(reply?.type ?? ''),
      id,
    };
  });
};

export const getAutoReplyPayload = (
  targetUserId: string,
  currentReply: AutoReplyV2 | AutoReply,
) => {
  if (!process.env.FEATURE_FLAG_AUTO_REPLIES_V2) {
    return [targetUserId, (currentReply as AutoReply).id];
  }
  return [
    targetUserId,
    convertToAutoReplyV2Type((currentReply as AutoReplyV2).type),
  ];
};

type UserState = {
  user: AuthState;
  _id: string;
  id: string;
};

type Action = {
  meta?: { entityIds: string[] };
  payload: AutoRepliesConfig;
};

export const updateAllAutoReplies = (state: UserState, action: Action) => {
  const raw = formatAutoReplyResponse(
    action?.payload,
    `USER#${action?.meta?.entityIds?.[0]}`,
  );
  const list = (Array.isArray(raw) ? raw : []) as Array<{
    type: string;
    active?: boolean;
  }>;
  const generalAutoRepliesList = filter(list, {
    type: AUTO_REPLY_TYPES.GENERAL,
  });
  const oooRepliesList = filter(list, {
    type: AUTO_REPLY_TYPES.OUT_OF_OFFICE,
  });
  return {
    ...state,
    userStatus: whatStatus(first(generalAutoRepliesList)),
    userAutoReply: {
      generalAutoReplies: { list: [...generalAutoRepliesList] },
      oooReplies: { list: [...oooRepliesList] },
    },
  };
};
